import React, { useEffect, useState } from "react";
import { addDoc, collection, deleteDoc, doc, getDocs, serverTimestamp, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../../../firebaseConfig";
import { OFFICE_DESIGNS } from "../../../utils/officeScheme/officeDesignTemplates";

export default function DistrictEstimateMaster() {
  const [form, setForm] = useState({
    districtName: "",
    designId: "DESIGN_300",
    estimateAmount: "",
    subsidyAmount: "",
    societyShare: "",
    status: "ACTIVE",
  });

  const [estimatePdf, setEstimatePdf] = useState(null);
  const [designPdf, setDesignPdf] = useState(null);
  const [rnbPdf, setRnbPdf] = useState(null);
  const [records, setRecords] = useState([]);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);

  const selectedDesign = OFFICE_DESIGNS.find((d) => d.id === form.designId);

  const fetchRecords = async () => {
    const snap = await getDocs(collection(db, "district_estimate_master"));
    setRecords(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const uploadFile = async (file, path) => {
    if (!file) return "";
    const fileRef = ref(storage, path);
    await uploadBytes(fileRef, file);
    return await getDownloadURL(fileRef);
  };

  const handleChange = (e) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const reset = () => {
    setForm({
      districtName: "",
      designId: "DESIGN_300",
      estimateAmount: "",
      subsidyAmount: "",
      societyShare: "",
      status: "ACTIVE",
    });
    setEstimatePdf(null);
    setDesignPdf(null);
    setRnbPdf(null);
    setEditId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.districtName || !form.designId || !form.estimateAmount) {
      alert("District, Design, Estimate Amount required.");
      return;
    }

    try {
      setLoading(true);
      const safeDistrict = form.districtName.trim().replaceAll(" ", "_");
      const basePath = `office_scheme_master/${safeDistrict}/${form.designId}`;

      const estimatePdfUrl = await uploadFile(estimatePdf, `${basePath}/estimate_${Date.now()}.pdf`);
      const designPdfUrl = await uploadFile(designPdf, `${basePath}/design_${Date.now()}.pdf`);
      const rnbVerifiedPdfUrl = await uploadFile(rnbPdf, `${basePath}/rnb_${Date.now()}.pdf`);

      const payload = {
        districtName: form.districtName.trim(),
        designId: form.designId,
        designTitle: selectedDesign?.title || "",
        designSize: selectedDesign?.sizeLabel || "",
        estimateAmount: Number(form.estimateAmount) || 0,
        subsidyAmount: Number(form.subsidyAmount) || 0,
        societyShare: Number(form.societyShare) || 0,
        status: form.status,
        updatedAt: serverTimestamp(),
      };

      if (estimatePdfUrl) payload.estimatePdfUrl = estimatePdfUrl;
      if (designPdfUrl) payload.designPdfUrl = designPdfUrl;
      if (rnbVerifiedPdfUrl) payload.rnbVerifiedPdfUrl = rnbVerifiedPdfUrl;

      if (editId) {
        await updateDoc(doc(db, "district_estimate_master", editId), payload);
      } else {
        await addDoc(collection(db, "district_estimate_master"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }

      alert("District estimate saved.");
      reset();
      fetchRecords();
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row) => {
    setEditId(row.id);
    setForm({
      districtName: row.districtName || "",
      designId: row.designId || "DESIGN_300",
      estimateAmount: row.estimateAmount || "",
      subsidyAmount: row.subsidyAmount || "",
      societyShare: row.societyShare || "",
      status: row.status || "ACTIVE",
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    await deleteDoc(doc(db, "district_estimate_master", id));
    fetchRecords();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow p-6">
        <h2 className="text-2xl font-bold">District Estimate Master</h2>
        <p className="text-sm text-slate-500">
          District + Design wise pre-uploaded estimate/design/R&B PDFs.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input label="District Name" name="districtName" value={form.districtName} onChange={handleChange} />

        <div>
          <label className="text-sm font-bold">Design Type</label>
          <select name="designId" value={form.designId} onChange={handleChange} className="w-full mt-1 border rounded-xl px-3 py-2">
            {OFFICE_DESIGNS.map((d) => (
              <option key={d.id} value={d.id}>
                {d.title} ({d.sizeLabel})
              </option>
            ))}
          </select>
        </div>

        <Input label="Estimate Amount" name="estimateAmount" value={form.estimateAmount} onChange={handleChange} type="number" />
        <Input label="Subsidy Amount" name="subsidyAmount" value={form.subsidyAmount} onChange={handleChange} type="number" />
        <Input label="Society Share" name="societyShare" value={form.societyShare} onChange={handleChange} type="number" />

        <div>
          <label className="text-sm font-bold">Status</label>
          <select name="status" value={form.status} onChange={handleChange} className="w-full mt-1 border rounded-xl px-3 py-2">
            <option value="ACTIVE">ACTIVE</option>
            <option value="INACTIVE">INACTIVE</option>
          </select>
        </div>

        <FileInput label="Estimate PDF" onChange={(e) => setEstimatePdf(e.target.files[0])} />
        <FileInput label="Design PDF" onChange={(e) => setDesignPdf(e.target.files[0])} />
        <FileInput label="R&B Verified PDF" onChange={(e) => setRnbPdf(e.target.files[0])} />

        <button disabled={loading} className="md:col-span-3 bg-emerald-600 text-white font-bold py-3 rounded-xl">
          {loading ? "Saving..." : editId ? "Update Master" : "Save Master"}
        </button>

        {editId && (
          <button type="button" onClick={reset} className="md:col-span-3 bg-slate-200 py-3 rounded-xl font-bold">
            Cancel Edit
          </button>
        )}
      </form>

      <div className="bg-white rounded-2xl shadow p-6 overflow-x-auto">
        <table className="w-full text-sm border">
          <thead className="bg-slate-100">
            <tr>
              <th className="border p-2">District</th>
              <th className="border p-2">Design</th>
              <th className="border p-2">Estimate</th>
              <th className="border p-2">Subsidy</th>
              <th className="border p-2">Share</th>
              <th className="border p-2">PDFs</th>
              <th className="border p-2">Status</th>
              <th className="border p-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r) => (
              <tr key={r.id}>
                <td className="border p-2">{r.districtName}</td>
                <td className="border p-2">{r.designSize}</td>
                <td className="border p-2">₹ {r.estimateAmount}</td>
                <td className="border p-2">₹ {r.subsidyAmount}</td>
                <td className="border p-2">₹ {r.societyShare}</td>
                <td className="border p-2">
                  {r.estimatePdfUrl && <a className="text-blue-600 mr-2" href={r.estimatePdfUrl} target="_blank" rel="noreferrer">Estimate</a>}
                  {r.designPdfUrl && <a className="text-blue-600 mr-2" href={r.designPdfUrl} target="_blank" rel="noreferrer">Design</a>}
                  {r.rnbVerifiedPdfUrl && <a className="text-blue-600" href={r.rnbVerifiedPdfUrl} target="_blank" rel="noreferrer">R&B</a>}
                </td>
                <td className="border p-2">{r.status}</td>
                <td className="border p-2">
                  <button onClick={() => handleEdit(r)} className="bg-amber-500 text-white px-3 py-1 rounded mr-2">Edit</button>
                  <button onClick={() => handleDelete(r.id)} className="bg-red-600 text-white px-3 py-1 rounded">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Input({ label, name, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="text-sm font-bold">{label}</label>
      <input name={name} value={value} onChange={onChange} type={type} className="w-full mt-1 border rounded-xl px-3 py-2" />
    </div>
  );
}

function FileInput({ label, onChange }) {
  return (
    <div>
      <label className="text-sm font-bold">{label}</label>
      <input type="file" accept="application/pdf" onChange={onChange} className="w-full mt-1 border rounded-xl px-3 py-2" />
    </div>
  );
}